/**
 * Methodology — Fee Status.
 *
 * Public page that explains how we think fee status works per country.
 * Content is served from `GET /api/fee-status/methodology` so a product
 * person can rewrite the summaries without a FE deploy. The page itself
 * is just the shell + disclaimer frame + per-country cards.
 *
 * Design notes:
 *   - Disclaimer sits above the fold. Non-negotiable.
 *   - Every country card ends in an authority link so students never
 *     have to trust our wording alone.
 *   - "Last reviewed" month surfaces drift risk — fee rules shift every
 *     academic year.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

export default function MethodologyFeeStatus() {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    api.get("/fee-status/methodology")
      .then(r => setData(r.data))
      .catch(e => setErr(e?.message || "Failed to load."));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-4xl mx-auto px-4 py-10">
        <Link
          to="/profile#profile-field-nationalities"
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-800 mb-6"
          data-testid="methodology-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to your profile
        </Link>

        <div className="text-[11px] font-underground-sc tracking-[0.18em] text-[#4C1D95] mb-1">
          Methodology
        </div>
        <h1 className="font-editorial text-4xl text-stone-900 mb-4">
          How we think about fee status
        </h1>

        {/* Disclaimer first — this page is specifically about caveats. */}
        <div
          data-testid="methodology-disclaimer"
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-5 flex gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-[13px] text-amber-950 leading-relaxed">
            {data?.disclaimer || "Guidelines only. Fee status is finally determined by the admitting university — always confirm with their admissions or finance office before relying on these figures."}
            {data?.last_reviewed && (
              <div className="mt-2 text-[11px] text-amber-800 font-mono">
                Last reviewed: {data.last_reviewed}
              </div>
            )}
          </div>
        </div>

        {err && (
          <div className="mt-6 text-[13px] text-amber-700">{err}</div>
        )}

        <div className="mt-8 space-y-5">
          {(data?.countries || []).map((c) => (
            <article
              key={c.code}
              data-testid={`methodology-country-${c.code}`}
              className="rounded-xl border border-stone-200 bg-white p-6"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h2 className="font-editorial text-2xl text-stone-900 flex items-center gap-2">
                    <span className="text-xl" aria-hidden>{c.flag}</span>
                    {c.name}
                  </h2>
                </div>
                <div className="text-right text-[11px] text-stone-500 font-mono shrink-0">
                  <div>Home: <span className="text-stone-900">{c.typical_home_fee}</span></div>
                  <div className="mt-0.5">Intl: <span className="text-stone-900">{c.typical_intl_fee}</span></div>
                </div>
              </div>

              <p className="text-[13.5px] text-stone-700 leading-relaxed">
                {c.summary}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px]">
                {c.authority && (
                  <a
                    href={c.authority.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`methodology-authority-${c.code}`}
                    className="inline-flex items-center gap-1 text-[#4C1D95] underline underline-offset-2 hover:text-[#2E1065]"
                  >
                    {c.authority.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {(c.also_see || []).map((link) => (
                  <a
                    key={link.url} href={link.url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-stone-500 underline underline-offset-2 hover:text-stone-800"
                  >
                    {link.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 border-t border-stone-200 pt-6 text-[12px] text-stone-500 leading-relaxed">
          <strong className="text-stone-700">Caveats, in plain English.</strong>{" "}
          These rules change every academic year. Edge cases we don't model yet:
          Graduate Route visa holders, refugees, dual nationals with
          complicated residence histories, Québec sub-rates, Welsh/NI fee
          variants, and Baden-Württemberg's non-EU fee at specific unis.
          If any of these apply, treat our fee tiles as a sanity check and
          rely on the university's own fee-status assessor for the number
          that actually appears on your invoice.
        </div>
      </div>
    </div>
  );
}
